import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { BaseResponse } from "app/business/common/baseresponse";
import { extractMasterPage } from "app/helper/masterpagehelper";
import { LookUpItem } from "app/business/common/lookupitem";
import { PaginationRequest } from "app/business/common/paginationrequest";
import { NotificationStatusInputItem } from "app/business/masterobject/notificationstatusinputitem";
import { AllergenContains } from "app/models/master/allergen-contains";
import { AllergenFree } from "app/models/master/allergen-free";
import { Allergen } from "app/models/master/allergen";
import { FinishProductAllergen } from "app/models/master/finish-product-allergen";
import { FinishProductObservedAllergen } from "app/models/master/finish-product-observed-allergen";
import { FinishProductObservedTolerance } from "app/models/master/finish-product-observed-tolerance";
import { ContainsDeclaration } from "app/models/master/contains-declaration";
import { FreeFromClaim } from "app/models/master/free-from-claim";
import { ContainsDeclarationIntolerance } from "app/models/master/contains-declaration-intolerance";
import { FreeFromClaimIntolerance } from "app/models/master/free-from-claim-intolerance";
import { Intolerance } from "app/models/master/intolerance";
import { CookingProcess } from "app/models/master/cookingprocess";
import { Cuisine } from "app/models/master/cuisine";
import { CuisineContinent } from "app/models/master/cuisinecontinent";
import { CuisineRegion } from "app/models/master/cuisineregion";
import { CuisineSubRegion } from "app/models/master/cuisinesubregion";
import { Equipment } from "app/models/master/equipment";
import { FoodGroup } from "app/models/master/foodgroup";
import { FoodGroupFAO } from "app/models/master/foodgroupFAO";
import { Ingredient } from "app/models/master/ingredient";
import { MealPreference } from "app/models/master/mealpreference";
import { MealType } from "app/models/master/mealtype";
import { Ocassion } from "app/models/master/ocassion";
import { Season } from "app/models/master/season";
import { RawMaterialType } from "app/models/master/raw-material-type";
import { IngredientsGlycemicIndex } from "app/models/master/ingredients-glycemic-index";
import { ProductType } from "app/models/master/product-type";
import { RecipeCategory } from "app/models/master/recipe-category";
import { Taste } from "app/models/master/taste";
import { Texture } from "app/models/master/texture";
import { Flavour } from "app/models/master/flavour";
import { Aroma } from "app/models/master/aroma";
import { tblCity } from "app/models/master/tblcity";
import { tblCountry } from "app/models/master/tblcountry";
import { tblLocation } from "app/models/master/tbllocation";
import { tblNotificationMaster } from "app/models/master/tblnotificationcenter";
import { tblPincode } from "app/models/master/tblpincode";
import { tblState } from "app/models/master/tblstate";
import { Utensil } from "app/models/master/utensil";
import { Unit } from "app/models/master/unit";
import { Nutrient } from "app/models/master/nutrient";
import { IngredientUnitOfMeasurement } from "app/models/master/ingredient-unit-of-measurement";
import { DietaryPreference } from "app/models/master/dietary-preference";
import { HealthColorCode } from "app/models/master/health-color-code";
import { DataSource } from "app/models/master/data-source";
import { DatabaseSource } from "app/models/master/database-source";
import { environment } from "environments/environment";
import { timeout } from "rxjs/operators";

@Injectable({
    providedIn: 'root',
})
export class MasterService {
    private readonly recipeMetadataCache = new Map<string, Promise<any>>();
    private readonly productMetadataCache = new Map<string, Promise<any>>();
    private ingredientMetadataPromise: Promise<BaseResponse> | null = null;

    constructor(private http: HttpClient) { }
    //#region Country
    async getCountries(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/GetCountries', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async getProductCountries(): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.get(
            `${environment.baseUrl}api/products/metadata/countries`,
            { headers }
        ).toPromise();
    }

    async addCountry(countryDetail: tblCountry): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/AddCountry', countryDetail)
            .toPromise() as BaseResponse;
    }

    async updateCountry(countryDetail: tblCountry): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/UpdateCountry', countryDetail)
            .toPromise() as BaseResponse;
    }

    async deleteCountry(countryId: number): Promise<BaseResponse> {
        return await this.http.get(environment.apiUrl + '/Master/DeleteCountry/' + countryId)
            .toPromise() as BaseResponse;
    }

    //#endregion
    //#region State
    async getStates(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/GetStates', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async addState(stateDetail: tblState): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/AddState', stateDetail)
            .toPromise() as BaseResponse;
    }

    async updateState(stateDetail: tblState): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/UpdateState', stateDetail)
            .toPromise() as BaseResponse;
    }

    async deleteState(stateId: number): Promise<BaseResponse> {
        return await this.http.get(environment.apiUrl + '/Master/DeleteState/' + stateId)
            .toPromise() as BaseResponse;
    }
    //#endregion
    //#region City
    async getCities(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/GetCities', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async addCity(cityDetail: tblCity): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/AddCity', cityDetail)
            .toPromise() as BaseResponse;
    }

    async updateCity(cityDetail: tblCity): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/UpdateCity', cityDetail)
            .toPromise() as BaseResponse;
    }

    async deleteCity(cityId: number): Promise<BaseResponse> {
        return await this.http.get(environment.apiUrl + '/Master/DeleteCity/' + cityId)
            .toPromise() as BaseResponse;
    }
    //#endregion
    //#region Pincode
    async getPincodes(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/GetPincodes', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async addPincode(pincodeDetail: tblPincode): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/AddPincode', pincodeDetail)
            .toPromise() as BaseResponse;
    }

    async updatePincode(pincodeDetail: tblPincode): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/UpdatePincode', pincodeDetail)
            .toPromise() as BaseResponse;
    }

    async deletePincode(pincodeId: number): Promise<BaseResponse> {
        return await this.http.get(environment.apiUrl + '/Master/DeletePincode/' + pincodeId)
            .toPromise() as BaseResponse;
    }
    //#endregion 

    //#region 
    async getLanguages(): Promise<BaseResponse> {
        return await this.http.get(environment.apiUrl + '/Master/GetLanguages')
            .toPromise() as BaseResponse;
    }
    //#endregion

    //#region Notification 
    async getNotificationMaster(): Promise<BaseResponse> {
        return await this.http.get(environment.apiUrl + '/Master/GetNotificationMaster')
            .toPromise() as BaseResponse;
    }

    async updateNotificationMaster(notificationDetail: tblNotificationMaster): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/UpdateNotificationMaster', notificationDetail)
            .toPromise() as BaseResponse;
    }

    async updateNotificationStatusById(input: NotificationStatusInputItem): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/UpdateNotificationStatusById', input)
            .toPromise() as BaseResponse;
    }

    async updateNotificationTitleById(input: LookUpItem): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + '/Master/UpdateNotificationTitleById', input)
            .toPromise() as BaseResponse;
    }

    //#endregion

    //#region Meal Preference
    async getMealPreference(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('mealpreference', paginationRequest);
    }

    async getDietaryPreferences(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('dietpreference', paginationRequest);
    }

    async getDietaryPreferenceById(id: string): Promise<BaseResponse> {
        return this.getMasterRecordById('dietpreference', id);
    }

    async addDietaryPreference(input: DietaryPreference): Promise<BaseResponse> {
        return this.addMasterRecord('dietpreference', input);
    }

    async updateDietaryPreference(input: DietaryPreference): Promise<BaseResponse> {
        return this.updateMasterRecord('dietpreference', input);
    }

    async deleteDietaryPreference(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('dietpreference', id);
    }

    async addMealPreference(input: MealPreference): Promise<BaseResponse> {
        return this.addMasterRecord('mealpreference', input);
    }

    async updateMealPreference(input: MealPreference): Promise<BaseResponse> {
        return this.updateMasterRecord('mealpreference', input);
    }

    async deleteMealPreference(id: number): Promise<BaseResponse> {
        return this.deleteMasterRecord('mealpreference', id.toString());
    }
    //#endregion

    //#region Meal Type
    async getMealType(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('mealtype', paginationRequest);
    }

    async addMealType(input: MealType): Promise<BaseResponse> {
        return this.addMasterRecord('mealtype', input);
    }

    async updateMealType(input: MealType): Promise<BaseResponse> {
        return this.updateMasterRecord('mealtype', input);
    }

    async deleteMealType(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('mealtype', id);
    }
    //#endregion

    //#region Food Group
    async getFoodGroup(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('foodgroup', paginationRequest);
    }

    async addFoodGroup(input: FoodGroup): Promise<BaseResponse> {
        return this.addMasterRecord('foodgroup', input);
    }

    async updateFoodGroup(input: FoodGroup): Promise<BaseResponse> {
        return this.updateMasterRecord('foodgroup', input);
    }

    async deleteFoodGroup(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('foodgroup', id);
    }
    //#endregion

    //#region Food Group FAO
    async getFoodGroupFAO(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('faofoodgroup', paginationRequest);
    }

    async addFoodGroupFAO(input: FoodGroupFAO): Promise<BaseResponse> {
        return this.addMasterRecord('faofoodgroup', input);
    }

    async updateFoodGroupFAO(input: FoodGroupFAO): Promise<BaseResponse> {
        return this.updateMasterRecord('faofoodgroup', input);
    }

    async deleteFoodGroupFAO(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('faofoodgroup', id);
    }

    async getFaoMealPreferenceMatrix(): Promise<BaseResponse> {
        return await this.http.get<BaseResponse>(environment.baseUrl + 'api/v1/recipes-pg/fao-meal-preference-matrix', { headers: this.getMasterHeaders() }).toPromise();
    }
    //#endregion

    //#region Taste
    async getTaste(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('taste', paginationRequest);
    }

    async addTaste(input: Taste): Promise<BaseResponse> {
        return this.addMasterRecord('taste', input);
    }

    async updateTaste(input: Taste): Promise<BaseResponse> {
        return this.updateMasterRecord('taste', input);
    }

    async deleteTaste(id: number): Promise<BaseResponse> {
        return this.deleteMasterRecord('taste', id.toString());
    }
    //#endregion

    //#region Texture
    async getTexture(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('texture', paginationRequest);
    }

    async addTexture(input: Texture): Promise<BaseResponse> {
        return this.addMasterRecord('texture', input);
    }

    async updateTexture(input: Texture): Promise<BaseResponse> {
        return this.updateMasterRecord('texture', input);
    }

    async deleteTexture(id: number): Promise<BaseResponse> {
        return this.deleteMasterRecord('texture', id.toString());
    }

    async getTextureById(id: string): Promise<BaseResponse> {
        return this.getMasterRecordById('texture', id);
    }
    //#endregion

    //#region Flavour
    async getFlavour(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('flavour', paginationRequest);
    }

    async addFlavour(input: Flavour): Promise<BaseResponse> {
        return this.addMasterRecord('flavour', input);
    }

    async updateFlavour(input: Flavour): Promise<BaseResponse> {
        return this.updateMasterRecord('flavour', input);
    }

    async deleteFlavour(id: number): Promise<BaseResponse> {
        return this.deleteMasterRecord('flavour', id.toString());
    }

    async getFlavourById(id: string): Promise<BaseResponse> {
        return this.getMasterRecordById('flavour', id);
    }
    //#endregion

    //#region Aroma
    async getAroma(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('aroma', paginationRequest);
    }

    async addAroma(input: Aroma): Promise<BaseResponse> {
        return this.addMasterRecord('aroma', input);
    }

    async updateAroma(input: Aroma): Promise<BaseResponse> {
        return this.updateMasterRecord('aroma', input);
    }

    async deleteAroma(id: number): Promise<BaseResponse> {
        return this.deleteMasterRecord('aroma', id.toString());
    }
    //#endregion

    //#region Recipe Category / Item Category
    async getRecipeCategory(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('itemcategory', paginationRequest);
    }

    async addRecipeCategory(input: RecipeCategory): Promise<BaseResponse> {
        return this.addMasterRecord('itemcategory', input);
    }

    async updateRecipeCategory(input: RecipeCategory): Promise<BaseResponse> {
        return this.updateMasterRecord('itemcategory', input);
    }

    async deleteRecipeCategory(id: number): Promise<BaseResponse> {
        return this.deleteMasterRecord('itemcategory', id.toString());
    }
    //#endregion

    //#region Season
    async getSeason(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('season', paginationRequest);
    }

    async addSeason(input: Season): Promise<BaseResponse> {
        return this.addMasterRecord('season', input);
    }

    async updateSeason(input: Season): Promise<BaseResponse> {
        return this.updateMasterRecord('season', input);
    }

    async deleteSeason(id: number): Promise<BaseResponse> {
        return this.deleteMasterRecord('season', id.toString());
    }
    //#endregion

    //#region Raw Material Type
    async getRawMaterialType(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('rawmaterialtype', paginationRequest);
    }

    async addRawMaterialType(input: RawMaterialType): Promise<BaseResponse> {
        const res = await this.addMasterRecord('rawmaterialtype', input);
        this.ingredientMetadataPromise = null;
        return res;
    }

    async updateRawMaterialType(input: RawMaterialType): Promise<BaseResponse> {
        const res = await this.updateMasterRecord('rawmaterialtype', input);
        this.ingredientMetadataPromise = null;
        return res;
    }

    async deleteRawMaterialType(id: number): Promise<BaseResponse> {
        const res = await this.deleteMasterRecord('rawmaterialtype', id.toString());
        this.ingredientMetadataPromise = null;
        return res;
    }
    //#endregion

    //#region Ingredients Glycemic Index
    async getGlycemicIndex(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('glycemicindex', paginationRequest);
    }

    async addGlycemicIndex(input: IngredientsGlycemicIndex): Promise<BaseResponse> {
        const res = await this.addMasterRecord('glycemicindex', input);
        this.ingredientMetadataPromise = null;
        return res;
    }

    async updateGlycemicIndex(input: IngredientsGlycemicIndex): Promise<BaseResponse> {
        const res = await this.updateMasterRecord('glycemicindex', input);
        this.ingredientMetadataPromise = null;
        return res;
    }

    async deleteGlycemicIndex(id: number): Promise<BaseResponse> {
        const res = await this.deleteMasterRecord('glycemicindex', id.toString());
        this.ingredientMetadataPromise = null;
        return res;
    }
    //#endregion

    //#region Product Type
    async getProductType(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('producttype', paginationRequest);
    }

    async addProductType(input: ProductType): Promise<BaseResponse> {
        const res = await this.addMasterRecord('producttype', input);
        this.clearProductMetadataCache();
        return res;
    }

    async updateProductType(input: ProductType): Promise<BaseResponse> {
        const res = await this.updateMasterRecord('producttype', input);
        this.clearProductMetadataCache();
        return res;
    }

    async deleteProductType(id: number): Promise<BaseResponse> {
        const res = await this.deleteMasterRecord('producttype', id.toString());
        this.clearProductMetadataCache();
        return res;
    }
    //#endregion

    //#region Ocassion
    async getOcassion(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('occasion', paginationRequest);
    }

    async addOcassion(input: Ocassion): Promise<BaseResponse> {
        return this.addMasterRecord('occasion', input);
    }

    async updateOcassion(input: Ocassion): Promise<BaseResponse> {
        return this.updateMasterRecord('occasion', input);
    }

    async deleteOcassion(id: number): Promise<BaseResponse> {
        return this.deleteMasterRecord('occasion', id.toString());
    }
    //#endregion

    //#region Location
    async getLocation(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        const pgResponse = await this.listMasterRecords('location', paginationRequest);
        const pgItems = extractMasterPage<any>(pgResponse).items;
        if (pgItems?.length) {
            return pgResponse;
        }

        // LocationMasters in Postgres may be empty until migrated — fall back to legacy Mongo master.
        return await this.http.post(
            environment.apiUrl + '/Master/GetLocation',
            paginationRequest,
            { headers: this.getMasterHeaders() }
        ).toPromise() as BaseResponse;
    }

    async addLocation(input: tblLocation): Promise<BaseResponse> {
        return this.addMasterRecord('location', input);
    }

    async updateLocation(input: tblLocation): Promise<BaseResponse> {
        return this.updateMasterRecord('location', input);
    }

    async deleteLocation(id: number): Promise<BaseResponse> {
        return this.deleteMasterRecord('location', id.toString());
    }
    //#endregion

    //#region Cooking Process
    async getCookingProcess(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('cookingmethod', paginationRequest);
    }

    async addCookingProcess(input: CookingProcess): Promise<BaseResponse> {
        return this.addMasterRecord('cookingmethod', input);
    }

    async updateCookingProcess(input: CookingProcess): Promise<BaseResponse> {
        return this.updateMasterRecord('cookingmethod', input);
    }

    async deleteCookingProcess(id: number): Promise<BaseResponse> {
        return this.deleteMasterRecord('cookingmethod', id.toString());
    }
    //#endregion

    //#region Storage Type
    async getStorageTypes(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('storage', paginationRequest);
    }

    async addStorageType(input: any): Promise<BaseResponse> {
        return this.addMasterRecord('storage', input);
    }

    async updateStorageType(input: any): Promise<BaseResponse> {
        return this.updateMasterRecord('storage', input);
    }

    async deleteStorageType(id: number): Promise<BaseResponse> {
        return this.deleteMasterRecord('storage', id.toString());
    }
    //#endregion

    //#region Reheating Method
    async getReheatingMethods(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('reheatingmethod', paginationRequest);
    }

    async addReheatingMethod(input: any): Promise<BaseResponse> {
        return this.addMasterRecord('reheatingmethod', input);
    }

    async updateReheatingMethod(input: any): Promise<BaseResponse> {
        return this.updateMasterRecord('reheatingmethod', input);
    }

    async deleteReheatingMethod(id: number): Promise<BaseResponse> {
        return this.deleteMasterRecord('reheatingmethod', id.toString());
    }
    //#endregion

    //#region Utensil
    async getUtensil(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/UtensilEquipment/GetAll', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async getUtensils(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/UtensilEquipment/GetUtensils', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async getEquipments(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/UtensilEquipment/GetEquipments', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async addUtensil(input: Utensil): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/UtensilEquipment/Add', input)
            .toPromise() as BaseResponse;
    }

    async updateUtensil(input: Utensil): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/UtensilEquipment/Update', input)
            .toPromise() as BaseResponse;
    }

    async deleteUtensil(id: number): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/UtensilEquipment/Delete', id)
            .toPromise() as BaseResponse;
    }
    //#endregion 

    //#region Cuisine Continent
    async getCuisineContinent(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('cuisinecontinent', paginationRequest);
    }

    async addCuisineContinent(input: CuisineContinent): Promise<BaseResponse> {
        return this.addMasterRecord('cuisinecontinent', input);
    }

    async updateCuisineContinent(input: CuisineContinent): Promise<BaseResponse> {
        return this.updateMasterRecord('cuisinecontinent', input);
    }

    async deleteCuisineContinent(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('cuisinecontinent', id);
    }
    //#endregion 

    //#region Cuisine Region
    async getCuisineRegion(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('cuisineregion', paginationRequest);
    }

    async addCuisineRegion(input: CuisineRegion): Promise<BaseResponse> {
        return this.addMasterRecord('cuisineregion', input);
    }

    async updateCuisineRegion(input: CuisineRegion): Promise<BaseResponse> {
        return this.updateMasterRecord('cuisineregion', input);
    }

    async deleteCuisineRegion(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('cuisineregion', id);
    }
    //#endregion 

    //#region Cuisine SubRegion
    async getCuisineSubRegion(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('cuisinesubregion', paginationRequest);
    }

    async getCuisineSubSubRegions(cuisineSubRegionId: string | number): Promise<BaseResponse> {
        const headers = {};
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/cuisine-sub-regions/${cuisineSubRegionId}/sub-sub-regions`,
            { headers }
        ).toPromise() as BaseResponse;
    }

    async addCuisineSubRegion(input: CuisineSubRegion): Promise<BaseResponse> {
        return this.addMasterRecord('cuisinesubregion', input);
    }

    async updateCuisineSubRegion(input: CuisineSubRegion): Promise<BaseResponse> {
        return this.updateMasterRecord('cuisinesubregion', input);
    }

    async deleteCuisineSubRegion(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('cuisinesubregion', id);
    }
    //#endregion 

    //#region Cuisine Sub SubRegion
    async getCuisineSubSubRegion(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('cuisinesubsubregion', paginationRequest);
    }

    async addCuisineSubSubRegion(input: any): Promise<BaseResponse> {
        return this.addMasterRecord('cuisinesubsubregion', input);
    }

    async updateCuisineSubSubRegion(input: any): Promise<BaseResponse> {
        return this.updateMasterRecord('cuisinesubsubregion', input);
    }

    async deleteCuisineSubSubRegion(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('cuisinesubsubregion', id);
    }
    //#endregion 

    //#region Cuisine 
    async getCuisine(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('cuisine', paginationRequest);
    }

    async getRegionWiseCuisines(recipeCuisineId: string | number): Promise<BaseResponse> {
        const headers = {};
        const id = Number(recipeCuisineId);
        const query = !Number.isFinite(id) || id <= 0 ? '' : `?recipeCuisineId=${id}`;
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/region-wise-cuisines${query}`,
            { headers }
        ).toPromise() as BaseResponse;
    }

    /** Reework-style cascade: continent ids → RecipeCuisine parent ids (name-aligned). */
    async getRecipeCuisineIdsByContinentIds(continentIds: number[]): Promise<any> {
        const headers = {};
        const ids = (continentIds || []).filter((id) => id > 0).join(',');
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/cuisine-taxonomy/recipe-cuisine-ids-by-continent-ids?ids=${ids}`,
            { headers }
        ).toPromise();
    }

    /** Formal geography regions under selected continents. */
    async getCuisineRegionsByContinentIds(continentIds: number[]): Promise<any> {
        const headers = {};
        const csv = (continentIds || []).filter((id) => id > 0).join(',');
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/cuisine-taxonomy/cuisine-regions-by-continent-ids?continentIds=${csv}`,
            { headers }
        ).toPromise();
    }

    /** Filtered sub-region rows (Reework region-wise cuisine list). Optional region labels = country names. */
    async getRegionWiseCuisinesForCascade(recipeCuisineIds: number[], regionLabels: string[] = []): Promise<any> {
        const headers = {};
        const rc = (recipeCuisineIds || []).filter((id) => id > 0).join(',');
        const regions = (regionLabels || [])
            .filter((label) => !!label && String(label).trim().length > 0)
            .map((label) => encodeURIComponent(String(label).trim()))
            .join(',');
        let url = environment.baseUrl + 'api/v1/recipes-pg/cuisine-taxonomy/region-wise-cuisines-for-cascade?';
        if (rc) {
            url += `recipeCuisineIds=${rc}`;
        }
        if (regions) {
            url += (rc ? '&' : '') + `regions=${regions}`;
        }
        return await this.http.get(url, { headers }).toPromise();
    }

    async getCuisineSubSubRegionsBySubRegionIds(subRegionIds: number[]): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/cuisine-taxonomy/sub-sub-regions/by-sub-region-ids',
            (subRegionIds || []).filter((id) => id > 0),
            { headers }
        ).toPromise();
    }

    /** Reverse cascade: cuisine (sub-sub) ids → sub-region + region + continent. */
    async getGeoParentsBySubSubRegionIds(subSubRegionIds: number[]): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/cuisine-taxonomy/geo-parents-by-sub-sub-region-ids',
            (subSubRegionIds || []).filter((id) => id > 0),
            { headers }
        ).toPromise();
    }

    /** Reverse cascade: sub-region ids → region + continent. */
    async getGeoParentsBySubRegionIds(subRegionIds: number[]): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/cuisine-taxonomy/geo-parents-by-sub-region-ids',
            (subRegionIds || []).filter((id) => id > 0),
            { headers }
        ).toPromise();
    }

    async getRegionWiseCuisineById(id: string): Promise<BaseResponse> {
        const headers = {};
        return await this.http.get(environment.baseUrl + `api/v1/recipes-pg/cuisine-regions/${id}`, { headers })
            .toPromise() as BaseResponse;
    }

    async addOrUpdateRegionWiseCuisine(input: any): Promise<BaseResponse> {
        const headers = {};
        return await this.http.post(environment.baseUrl + 'api/v1/recipes-pg/cuisine-regions', input, { headers })
            .toPromise() as BaseResponse;
    }

    async deleteRegionWiseCuisine(id: string | number): Promise<BaseResponse> {
        const headers = {};
        return await this.http.delete(environment.baseUrl + `api/v1/recipes-pg/cuisine-regions/${id}`, { headers })
            .toPromise() as BaseResponse;
    }

    async addCuisine(input: Cuisine): Promise<BaseResponse> {
        return this.addMasterRecord('cuisine', input);
    }

    async updateCuisine(input: Cuisine): Promise<BaseResponse> {
        return this.updateMasterRecord('cuisine', input);
    }

    async deleteCuisine(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('cuisine', id);
    }
    //#endregion 

    //#region Ingredient
    async getIngredients(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        const hasSearch = !!(paginationRequest?.searchString && paginationRequest.searchString.trim().length > 0);
        const hasFilters = this.hasIngredientSearchFilters(paginationRequest);
        const endpoint = (hasSearch || hasFilters)
            ? 'api/v1/recipes-pg/ingredients/search'
            : 'api/v1/recipes-pg/ingredients/list';
        return await this.http.post(environment.baseUrl + endpoint, paginationRequest, { headers: this.getMasterHeaders() })
            .toPromise() as BaseResponse;
    }

    private hasIngredientSearchFilters(paginationRequest?: PaginationRequest): boolean {
        if (!paginationRequest) {
            return false;
        }
        return !!(
            paginationRequest.dataSource ||
            paginationRequest.ingredientStatus ||
            paginationRequest.mealPreference ||
            paginationRequest.isVeg === true ||
            paginationRequest.isVeg === false ||
            (paginationRequest.healthColorCodeIds && paginationRequest.healthColorCodeIds.length > 0) ||
            (paginationRequest.foodGroupIds && paginationRequest.foodGroupIds.length > 0) ||
            (paginationRequest.faoFoodGroupIds && paginationRequest.faoFoodGroupIds.length > 0) ||
            (paginationRequest.seasonIds && paginationRequest.seasonIds.length > 0) ||
            (paginationRequest.allergenIds && paginationRequest.allergenIds.length > 0) ||
            (paginationRequest.intoleranceIds && paginationRequest.intoleranceIds.length > 0) ||
            (paginationRequest.rawTypeIds && paginationRequest.rawTypeIds.length > 0) ||
            (paginationRequest.itemTypeIds && paginationRequest.itemTypeIds.length > 0) ||
            (paginationRequest.nutrientTagIds && paginationRequest.nutrientTagIds.length > 0) ||
            (paginationRequest.nutrientGroupIds && paginationRequest.nutrientGroupIds.length > 0) ||
            (paginationRequest.fssaiNutrientTagMasterIds && paginationRequest.fssaiNutrientTagMasterIds.length > 0) ||
            paginationRequest.hasImage === true ||
            paginationRequest.hasImage === false ||
            !!paginationRequest.nutrientSortBy ||
            !!paginationRequest.sourcePlatform ||
            paginationRequest.duplicatesOnly === true
        );
    }

    async addIngredient(input: Ingredient): Promise<BaseResponse> {
        return await this.http.post(environment.baseUrl + 'api/v1/recipes-pg/ingredients', input, { headers: this.getMasterHeaders() })
            .toPromise() as BaseResponse;
    }

    async updateIngredient(input: Ingredient): Promise<BaseResponse> {
        return await this.http.put(environment.baseUrl + 'api/v1/recipes-pg/ingredients', input, { headers: this.getMasterHeaders() })
            .toPromise() as BaseResponse;
    }

    async bulkUpdateIngredients(body: {
        sourcePlatform: 'REEWORK';
        ids: number[];
        patch: {
            healthColorCodeId?: number | null;
            mealPreference?: string | null;
            isVeg?: boolean | null;
            foodGroupId?: number | null;
            foodGroupFAOId?: number | null;
            seasonIds?: number[] | null;
            status?: string | null;
            allergenContainsIds?: number[] | null;
            allergenFreeIds?: number[] | null;
            intoleranceContainsIds?: number[] | null;
            intoleranceFreeIds?: number[] | null;
        };
    }): Promise<BaseResponse> {
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/ingredients/bulk-update',
            body,
            { headers: this.getMasterHeaders() }
        ).toPromise() as BaseResponse;
    }

    /** One request for many full ingredient updates (bulk-edit preview confirm). */
    async bulkUpdateIngredientRows(rows: Ingredient[]): Promise<BaseResponse> {
        const payload = Array.isArray(rows) ? rows : [];
        if (!payload.length) {
            return { success: false, status: -1, message: 'No ingredients provided.' } as BaseResponse;
        }
        return await this.http
            .put(environment.baseUrl + 'api/v1/recipes-pg/ingredients/bulk', payload, {
                headers: this.getMasterHeaders(),
            })
            .pipe(timeout(600000))
            .toPromise() as BaseResponse;
    }

    async deleteIngredient(id: string): Promise<BaseResponse> {
        return await this.http.delete<BaseResponse>(environment.baseUrl + `api/v1/recipes-pg/ingredients/${id}`, { headers: this.getMasterHeaders() })
            .toPromise();
    }

    exportIngredientsWithoutUom(sourcePlatform: 'ALL' | 'RDMS' | 'REEWORK' | 'FITTERFLY' | 'FITTERFLY_ADDITIVE' | 'USDA' = 'ALL'): Promise<void> {
        const params = sourcePlatform && sourcePlatform !== 'ALL'
            ? `?sourcePlatform=${encodeURIComponent(sourcePlatform)}`
            : '';
        const tabLabel = !sourcePlatform || sourcePlatform === 'ALL' ? 'all' : sourcePlatform.toLowerCase();

        return new Promise((resolve, reject) => {
            this.http.get(environment.baseUrl + `api/v1/recipes-pg/ingredients/missing-uom/export${params}`, {
                headers: this.getMasterHeaders(),
                responseType: 'blob'
            }).subscribe({
                next: (blob: Blob) => {
                    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `ingredients_missing_uoms_${tabLabel}_${today}.csv`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                    resolve();
                },
                error: (err) => {
                    console.error('Failed to export ingredients without UOM:', err);
                    reject(err);
                }
            });
        });
    }

    exportIngredientsFullCsv(request: any, filename?: string): Promise<void> {
        const tabLabel = !request?.sourcePlatform || request.sourcePlatform === 'ALL'
            ? 'all'
            : String(request.sourcePlatform).toLowerCase();

        return new Promise((resolve, reject) => {
            this.http.post(environment.baseUrl + `api/v1/recipes-pg/ingredients/export-csv`, request, {
                headers: this.getMasterHeaders(),
                responseType: 'blob'
            }).subscribe({
                next: (blob: Blob) => {
                    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename || `ingredients_${tabLabel}_full_${today}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    resolve();
                },
                error: (err) => {
                    console.error('Failed to export ingredients full CSV:', err);
                    reject(err);
                }
            });
        });
    }

    async getIngredientById(id: string, sourcePlatform?: 'RDMS' | 'REEWORK' | string): Promise<BaseResponse> {
        const platform = String(sourcePlatform || '').trim().toUpperCase();
        const qs = platform ? `?sourcePlatform=${encodeURIComponent(platform)}` : '';
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/ingredients/${id}${qs}`,
            { headers: this.getMasterHeaders() }
        ).toPromise() as BaseResponse;
    }

    /** One request for many ingredient details (bulk-edit CSV download / upload preview). */
    async bulkGetIngredients(
        ids: Array<number | string>,
        sourcePlatform?: 'RDMS' | 'REEWORK' | 'USDA' | string
    ): Promise<BaseResponse> {
        const uniqueIds = Array.from(
            new Set(
                (ids || [])
                    .map((id) => Number(id))
                    .filter((id) => Number.isFinite(id) && id > 0)
            )
        );
        if (!uniqueIds.length) {
            return { success: false, status: -1, message: 'No ingredient ids provided.' } as BaseResponse;
        }
        const platform = String(sourcePlatform || '').trim().toUpperCase() || undefined;
        return await this.http
            .post(
                environment.baseUrl + 'api/v1/recipes-pg/ingredients/bulk-get',
                { ids: uniqueIds, sourcePlatform: platform },
                { headers: this.getMasterHeaders() }
            )
            .pipe(timeout(600000))
            .toPromise() as BaseResponse;
    }

    async searchLinkableFinishProducts(search?: string, pageNo = 1, pageSize = 20): Promise<BaseResponse> {
        let params = new HttpParams()
            .set('pageNo', String(pageNo))
            .set('pageSize', String(pageSize));
        if (search && search.trim()) {
            params = params.set('search', search.trim());
        }
        return await this.http.get(
            environment.baseUrl + 'api/v1/recipes-pg/ingredients/linkable-finish-products',
            { headers: this.getMasterHeaders(), params }
        ).toPromise() as BaseResponse;
    }

    async mapIngredientFromFinishProduct(finishProductId: number | string, source?: string): Promise<BaseResponse> {
        let params = new HttpParams();
        if (source) {
            params = params.set('source', source);
        }
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/ingredients/from-finish-product/${finishProductId}`,
            { headers: this.getMasterHeaders(), params }
        ).toPromise() as BaseResponse;
    }

    async getIngredientMetadata(forceRefresh: boolean = false): Promise<BaseResponse> {
        if (forceRefresh) {
            this.ingredientMetadataPromise = null;
        }
        if (this.ingredientMetadataPromise) {
            return this.ingredientMetadataPromise;
        }
        this.ingredientMetadataPromise = this.http
            .get(environment.apiUrl + '/ingredients/metadata', { headers: this.getMasterHeaders() })
            .toPromise() as Promise<BaseResponse>;
        try {
            return await this.ingredientMetadataPromise;
        } catch (error) {
            this.ingredientMetadataPromise = null;
            throw error;
        }
    }

    /** IFCT / USDA allergen Y/N matrix lookup → contains + free IngredientAllergenMaster ids. */
    async lookupAllergenSourceMatrix(params: {
        source: string;
        code?: string;
        name?: string;
    }): Promise<BaseResponse> {
        let url = environment.apiUrl + '/AllergenSourceMatrix/lookup?source=' + encodeURIComponent(params.source || '');
        if (params.code) {
            url += '&code=' + encodeURIComponent(params.code);
        }
        if (params.name) {
            url += '&name=' + encodeURIComponent(params.name);
        }
        return await this.http.get(url, { headers: this.getMasterHeaders() }).toPromise() as BaseResponse;
    }

    async searchAllergenSourceMatrix(source: string, q: string, limit = 20): Promise<BaseResponse> {
        const url =
            environment.apiUrl +
            '/AllergenSourceMatrix/search?source=' +
            encodeURIComponent(source || '') +
            '&q=' +
            encodeURIComponent(q || '') +
            '&limit=' +
            encodeURIComponent(String(limit));
        return await this.http.get(url, { headers: this.getMasterHeaders() }).toPromise() as BaseResponse;
    }

    async getNutrientTagsByGroupId(nutrientGroupId: string): Promise<BaseResponse> {
        const groupId = String(nutrientGroupId || '').trim();
        if (!groupId) {
            return { success: true, status: 0, response: { nutrientTagList: [] }, data: [] } as any;
        }

        // Prefer Postgres recipes-pg endpoint (Mongo /api/ingredients/... can return empty for PG group ids).
        try {
            const pgRes: any = await this.http.get(
                environment.baseUrl + `api/v1/recipes-pg/nutrient-groups/${encodeURIComponent(groupId)}/tags`,
                { headers: this.getMasterHeaders() }
            ).toPromise();
            const pgList = this.extractNutrientTagList(pgRes);
            if (pgList.length) {
                return {
                    success: true,
                    status: 0,
                    response: { nutrientTagList: pgList },
                    data: pgList
                } as any;
            }
        } catch (err) {
            console.warn('Postgres nutrient tags by group failed, trying legacy endpoint', err);
        }

        try {
            const legacyRes: any = await this.http.get(
                environment.apiUrl + `/ingredients/nutrient-groups/${encodeURIComponent(groupId)}/tags`,
                { headers: this.getMasterHeaders() }
            ).toPromise();
            const legacyList = this.extractNutrientTagList(legacyRes);
            if (legacyList.length) {
                return {
                    success: true,
                    status: 0,
                    response: { nutrientTagList: legacyList },
                    data: legacyList
                } as any;
            }
        } catch (err) {
            console.warn('Legacy nutrient tags by group failed, trying master list fallback', err);
        }

        // Final fallback: recipes-pg master nutrienttag list filtered by parentId/groupId
        try {
            const pagination = new PaginationRequest();
            pagination.pageSize = 2000;
            pagination.pageNo = 1;
            pagination.searchString = '';
            const masterRes: any = await this.getNutrientTag(pagination);
            const all = this.extractNutrientTagList(masterRes);
            const filtered = all.filter((t: any) =>
                String(t.parentId ?? t.ParentId ?? t.groupId ?? t.GroupId ?? t.nutrientGroupId ?? '') === groupId
            );
            return {
                success: true,
                status: 0,
                response: { nutrientTagList: filtered },
                data: filtered
            } as any;
        } catch (err) {
            console.error('Failed to load nutrient tags for group', groupId, err);
            return { success: false, status: 1, response: { nutrientTagList: [] }, data: [] } as any;
        }
    }

    private extractNutrientTagList(response: any): any[] {
        if (!response) return [];
        if (Array.isArray(response)) return response;
        const root = response.response ?? response.data ?? response.result ?? response;
        if (Array.isArray(root)) return root;
        const keys = ['nutrientTagList', 'nutrientTags', 'tags', 'items', 'records', 'list', 'data'];
        for (const key of keys) {
            if (Array.isArray(root?.[key])) return root[key];
            if (Array.isArray(response?.[key])) return response[key];
        }
        if (root && typeof root === 'object') {
            for (const value of Object.values(root)) {
                if (Array.isArray(value)) return value as any[];
            }
        }
        return [];
    }
    //#endregion

    //#region Nutrient Groups
    async getNutrientGroup(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('nutrientgroup', paginationRequest);
    }

    async addNutrientGroup(input: any): Promise<BaseResponse> {
        return this.addMasterRecord('nutrientgroup', input);
    }

    async updateNutrientGroup(input: any): Promise<BaseResponse> {
        return this.updateMasterRecord('nutrientgroup', input);
    }

    async deleteNutrientGroup(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('nutrientgroup', id);
    }
    //#endregion

    //#region Nutrient Tags
    async getNutrientTag(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('nutrienttag', paginationRequest);
    }

    async addNutrientTag(input: any): Promise<BaseResponse> {
        return this.addMasterRecord('nutrienttag', input);
    }

    async updateNutrientTag(input: any): Promise<BaseResponse> {
        return this.updateMasterRecord('nutrienttag', input);
    }

    async deleteNutrientTag(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('nutrienttag', id);
    }
    //#endregion

    //#region Disease Categories
    async getDiseaseCategory(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('diseasecategory', paginationRequest);
    }

    async addDiseaseCategory(input: any): Promise<BaseResponse> {
        return this.addMasterRecord('diseasecategory', input);
    }

    async updateDiseaseCategory(input: any): Promise<BaseResponse> {
        return this.updateMasterRecord('diseasecategory', input);
    }

    async deleteDiseaseCategory(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('diseasecategory', id);
    }
    //#endregion

    //#region Product Forms
    async getProductForm(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('productform', paginationRequest);
    }

    async addProductForm(input: any): Promise<BaseResponse> {
        return this.addMasterRecord('productform', input);
    }

    async updateProductForm(input: any): Promise<BaseResponse> {
        return this.updateMasterRecord('productform', input);
    }

    async deleteProductForm(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('productform', id);
    }
    //#endregion

    //#region Diseases
    async getDisease(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('disease', paginationRequest);
    }

    async addDisease(input: any): Promise<BaseResponse> {
        return this.addMasterRecord('disease', input);
    }

    async updateDisease(input: any): Promise<BaseResponse> {
        return this.updateMasterRecord('disease', input);
    }

    async deleteDisease(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('disease', id);
    }
    //#endregion

    //#region Allergen Contains
    async getAllergenContains(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/GetAllergenContains', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async addAllergenContains(input: AllergenContains): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/AddAllergenContains', input)
            .toPromise() as BaseResponse;
    }

    async updateAllergenContains(input: AllergenContains): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/UpdateAllergenContains', input)
            .toPromise() as BaseResponse;
    }

    async deleteAllergenContains(id: number): Promise<BaseResponse> {
        return await this.http.get(environment.masterApiUrl + '/Master/DeleteAllergenContains/' + id)
            .toPromise() as BaseResponse;
    }
    //#endregion

    //#region Allergen Free
    async getAllergenFree(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/GetAllergenFree', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async addAllergenFree(input: AllergenFree): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/AddAllergenFree', input)
            .toPromise() as BaseResponse;
    }

    async updateAllergenFree(input: AllergenFree): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/UpdateAllergenFree', input)
            .toPromise() as BaseResponse;
    }

    async deleteAllergenFree(id: number): Promise<BaseResponse> {
        return await this.http.get(environment.masterApiUrl + '/Master/DeleteAllergenFree/' + id)
            .toPromise() as BaseResponse;
    }
    //#endregion

    //#region Allergen (Unified)
    // Dynamic common helpers for master records
    private getMasterHeaders() {
        return {};
    }

    async listMasterRecords(endpoint: string, paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.baseUrl + `api/v1/recipes-pg/master/${endpoint}/list`, paginationRequest, { headers: this.getMasterHeaders() })
            .toPromise() as BaseResponse;
    }

    async getMasterRecordById(endpoint: string, id: string): Promise<BaseResponse> {
        const headers = {};
        return await this.http.get(environment.baseUrl + `api/v1/recipes-pg/master/${endpoint}/${id}`, { headers })
            .toPromise() as BaseResponse;
    }

    async addMasterRecord(endpoint: string, input: any): Promise<BaseResponse> {
        return await this.http.post(environment.baseUrl + `api/v1/recipes-pg/master/${endpoint}`, input, { headers: this.getMasterHeaders() })
            .toPromise() as BaseResponse;
    }

    async updateMasterRecord(endpoint: string, input: any): Promise<BaseResponse> {
        return await this.http.put(environment.baseUrl + `api/v1/recipes-pg/master/${endpoint}`, input, { headers: this.getMasterHeaders() })
            .toPromise() as BaseResponse;
    }

    async deleteMasterRecord(endpoint: string, id: string): Promise<BaseResponse> {
        const headers = {};
        return await this.http.delete(environment.baseUrl + `api/v1/recipes-pg/master/${endpoint}/${id}`, { headers })
            .toPromise() as BaseResponse;
    }

    // Allergen Specific Implementations using the generic helpers
    async getAllergens(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('ingredientallergen', paginationRequest);
    }

    async uploadMedia(file: File, folderName: string): Promise<string> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folderName', folderName);

        const headers = {
            'accept': '*/*'
        };

        try {
            const uploadRes: any = await this.http.post(environment.baseUrl + 'api/v1/recipes-pg/media/upload', formData, { headers }).toPromise();
            if (uploadRes && (uploadRes.success || uploadRes.status === 0)) {
                if (typeof uploadRes.data === 'string') return uploadRes.data;
                if (uploadRes.data) return uploadRes.data.url || uploadRes.data.fileUrl || uploadRes.data.filePath || uploadRes.data.imagePath || uploadRes.data.ImagePath || '';
                return uploadRes.url || uploadRes.fileUrl || uploadRes.ImagePath || uploadRes.imagePath || '';
            }
        } catch (err) {
            console.error('Upload failed', err);
        }
        return '';
    }

    async addAllergen(input: Allergen): Promise<BaseResponse> {
        return this.addMasterRecord('ingredientallergen', input);
    }

    async updateAllergen(input: Allergen): Promise<BaseResponse> {
        return this.updateMasterRecord('ingredientallergen', input);
    }

    async deleteAllergen(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('ingredientallergen', id);
    }

    /** PostgreSQL AllergenFreeMasters via recipes-pg (not legacy Mongo /Master/AddAllergenFree). */
    async getAllergenFreeMaster(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('allergenfree', paginationRequest);
    }

    async addAllergenFreeMaster(input: { id?: string | number; name: string; isActive?: boolean }): Promise<BaseResponse> {
        return this.addMasterRecord('allergenfree', input);
    }

    async updateAllergenFreeMaster(input: { id?: string | number; name: string; isActive?: boolean }): Promise<BaseResponse> {
        return this.updateMasterRecord('allergenfree', input);
    }

    async deleteAllergenFreeMaster(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('allergenfree', id);
    }
    //#endregion

    //#region Finish Product Allergen
    async getFinishProductAllergens(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/GetFinishProductAllergens', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async addFinishProductAllergen(input: FinishProductAllergen): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/AddFinishProductAllergen', input)
            .toPromise() as BaseResponse;
    }

    async updateFinishProductAllergen(input: FinishProductAllergen): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/UpdateFinishProductAllergen', input)
            .toPromise() as BaseResponse;
    }

    async deleteFinishProductAllergen(id: string): Promise<BaseResponse> {
        return await this.http.get(environment.masterApiUrl + '/Master/DeleteFinishProductAllergen/' + id)
            .toPromise() as BaseResponse;
    }
    //#endregion

    //#region Finish Product Observed Allergen
    async getFinishProductObservedAllergens(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/GetFinishProductObservedAllergens', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async addFinishProductObservedAllergen(input: FinishProductObservedAllergen): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/AddFinishProductObservedAllergen', input)
            .toPromise() as BaseResponse;
    }

    async updateFinishProductObservedAllergen(input: FinishProductObservedAllergen): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/UpdateFinishProductObservedAllergen', input)
            .toPromise() as BaseResponse;
    }

    async deleteFinishProductObservedAllergen(id: string): Promise<BaseResponse> {
        return await this.http.get(environment.masterApiUrl + '/Master/DeleteFinishProductObservedAllergen/' + id)
            .toPromise() as BaseResponse;
    }
    //#endregion

    //#region Finish Product Observed Tolerance
    async getFinishProductObservedTolerances(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/GetFinishProductObservedTolerances', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async addFinishProductObservedTolerance(input: FinishProductObservedTolerance): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/AddFinishProductObservedTolerance', input)
            .toPromise() as BaseResponse;
    }

    async updateFinishProductObservedTolerance(input: FinishProductObservedTolerance): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/UpdateFinishProductObservedTolerance', input)
            .toPromise() as BaseResponse;
    }

    async deleteFinishProductObservedTolerance(id: string): Promise<BaseResponse> {
        return await this.http.get(environment.masterApiUrl + '/Master/DeleteFinishProductObservedTolerance/' + id)
            .toPromise() as BaseResponse;
    }
    //#endregion

    //#region Contains Declaration Intolerance
    async getContainsDeclarationIntolerances(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/GetContainsDeclarationIntolerances', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async addContainsDeclarationIntolerance(input: Partial<ContainsDeclarationIntolerance>): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/AddContainsDeclarationIntolerance', input)
            .toPromise() as BaseResponse;
    }

    async updateContainsDeclarationIntolerance(input: ContainsDeclarationIntolerance): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/UpdateContainsDeclarationIntolerance', input)
            .toPromise() as BaseResponse;
    }

    async deleteContainsDeclarationIntolerance(id: string): Promise<BaseResponse> {
        return await this.http.get(environment.masterApiUrl + '/Master/DeleteContainsDeclarationIntolerance/' + id)
            .toPromise() as BaseResponse;
    }
    //#endregion

    //#region Free From Claim Intolerance
    async getFreeFromClaimIntolerances(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/GetFreeFromClaimIntolerances', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async addFreeFromClaimIntolerance(input: Partial<FreeFromClaimIntolerance>): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/AddFreeFromClaimIntolerance', input)
            .toPromise() as BaseResponse;
    }

    async updateFreeFromClaimIntolerance(input: FreeFromClaimIntolerance): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/UpdateFreeFromClaimIntolerance', input)
            .toPromise() as BaseResponse;
    }

    async deleteFreeFromClaimIntolerance(id: string): Promise<BaseResponse> {
        return await this.http.get(environment.masterApiUrl + '/Master/DeleteFreeFromClaimIntolerance/' + id)
            .toPromise() as BaseResponse;
    }
    //#endregion

    //#region Contains Declaration
    async getContainsDeclarations(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/GetContainsDeclarations', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async addContainsDeclaration(input: ContainsDeclaration): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/AddContainsDeclaration', input)
            .toPromise() as BaseResponse;
    }

    async updateContainsDeclaration(input: ContainsDeclaration): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/UpdateContainsDeclaration', input)
            .toPromise() as BaseResponse;
    }

    async deleteContainsDeclaration(id: string): Promise<BaseResponse> {
        return await this.http.delete(environment.masterApiUrl + '/Master/DeleteContainsDeclaration/' + id)
            .toPromise() as BaseResponse;
    }
    //#endregion

    //#region Free From Claim
    async getFreeFromClaims(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/GetFreeFromClaims', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async addFreeFromClaim(input: FreeFromClaim): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/AddFreeFromClaim', input)
            .toPromise() as BaseResponse;
    }

    async updateFreeFromClaim(input: FreeFromClaim): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/UpdateFreeFromClaim', input)
            .toPromise() as BaseResponse;
    }

    async deleteFreeFromClaim(id: string): Promise<BaseResponse> {
        return await this.http.delete(environment.masterApiUrl + '/Master/DeleteFreeFromClaim/' + id)
            .toPromise() as BaseResponse;
    }
    //#endregion

    //#region Intolerance (PostgreSQL — Contains tab)
    async getIntolerances(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('intolerance', paginationRequest);
    }

    async addIntolerance(input: { id?: string | number; name: string; isActive?: boolean }): Promise<BaseResponse> {
        return this.addMasterRecord('intolerance', input);
    }

    async updateIntolerance(input: { id?: string | number; name: string; isActive?: boolean }): Promise<BaseResponse> {
        return this.updateMasterRecord('intolerance', input);
    }

    async deleteIntolerance(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('intolerance', id);
    }

    /** PostgreSQL IntoleranceMasters where IsFree = true (same table as contains). */
    async getIntoleranceFreeMaster(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('intolerancefree', paginationRequest);
    }

    async addIntoleranceFreeMaster(input: { id?: string | number; name: string; isActive?: boolean }): Promise<BaseResponse> {
        return this.addMasterRecord('intolerancefree', input);
    }

    async updateIntoleranceFreeMaster(input: { id?: string | number; name: string; isActive?: boolean }): Promise<BaseResponse> {
        return this.updateMasterRecord('intolerancefree', input);
    }

    async deleteIntoleranceFreeMaster(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('intolerancefree', id);
    }
    //#endregion

    //#region Unit
    async getUnits(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('servingunit', paginationRequest);
    }

    async getServingUnits(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('servingunit', paginationRequest);
    }

    async getSupplements(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        const headers = {};
        return await this.http.post(environment.baseUrl + 'api/v1/recipes-pg/supplements/list', paginationRequest, { headers })
            .toPromise() as BaseResponse;
    }

    async getSupplementById(id: string): Promise<BaseResponse> {
        const headers = {};
        return await this.http.get(environment.baseUrl + `api/v1/recipes-pg/supplements/${id}`, { headers })
            .toPromise() as BaseResponse;
    }

    async deleteSupplement(id: string): Promise<BaseResponse> {
        const headers = {};
        return await this.http.delete(environment.baseUrl + `api/v1/recipes-pg/supplements/${id}`, { headers })
            .toPromise() as BaseResponse;
    }

    async getIngredientNutritionParams(id: string, sourcePlatform?: string): Promise<BaseResponse> {
        const headers = {};
        let url = environment.baseUrl + `api/v1/recipes-pg/ingredients/${id}/nutrition-params`;
        const platform = String(sourcePlatform || '').trim().toUpperCase();
        if (platform === 'RDMS' || platform === 'REEWORK' || platform === 'FITTERFLY' || platform === 'FITTERFLY_ADDITIVE') {
            url += `?sourcePlatform=${encodeURIComponent(platform)}`;
        }
        return await this.http.get(url, { headers })
            .toPromise() as BaseResponse;
    }

    async getIngredientServingUnits(ingredientId: number): Promise<BaseResponse> {
        const headers = {};
        return await this.http.get(environment.baseUrl + `api/v1/recipes-pg/ingredients/${ingredientId}/serving-units`, { headers })
            .toPromise() as BaseResponse;
    }

    /** Recipes that use this ingredient (Raw Ingredient → Linked Recipe popup). */
    async getLinkedRecipesForIngredient(
        ingredientId: number | string,
        opts?: { pageNo?: number; pageSize?: number; searchString?: string; sortBy?: string; sortDirection?: string }
    ): Promise<BaseResponse> {
        const pageNo = opts?.pageNo ?? 1;
        const pageSize = opts?.pageSize ?? 10;
        const params = new URLSearchParams();
        params.set('pageNo', String(pageNo));
        params.set('pageSize', String(pageSize));
        if (opts?.searchString?.trim()) {
            params.set('searchString', opts.searchString.trim());
        }
        if (opts?.sortBy?.trim()) {
            params.set('sortBy', opts.sortBy.trim());
        }
        if (opts?.sortDirection?.trim()) {
            params.set('sortDirection', opts.sortDirection.trim());
        }
        return await this.http
            .get(
                environment.baseUrl +
                    `api/v1/recipes-pg/ingredients/${ingredientId}/linked-recipes?${params.toString()}`,
                { headers: this.getMasterHeaders() }
            )
            .toPromise() as BaseResponse;
    }

    async searchIngredientsForRecipe(searchString: string, pageNo: number = 1, pageSize: number = 30): Promise<BaseResponse> {
        const headers = {};
        return await this.http.post(environment.baseUrl + 'api/v1/recipes-pg/ingredients/search', {
            searchString,
            pageNo,
            pageSize
        }, { headers }).toPromise() as BaseResponse;
    }

    /**
     * Bulk-resolve many ingredient display names in one API call (recipe bulk upload).
     * POST api/v1/recipes-pg/ingredients/resolve-by-names
     */
    async resolveIngredientsByNames(names: string[]): Promise<BaseResponse> {
        const headers = {};
        const unique = Array.from(
            new Set((names || []).map((n) => String(n || '').trim()).filter(Boolean))
        );
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/ingredients/resolve-by-names',
            { names: unique, candidatesPerName: 12 },
            { headers }
        ).toPromise() as BaseResponse;
    }

    async addUnit(input: Unit): Promise<BaseResponse> {
        return this.addMasterRecord('servingunit', input);
    }

    async addServingUnit(input: Unit): Promise<BaseResponse> {
        return this.addMasterRecord('servingunit', input);
    }

    async updateUnit(input: Unit): Promise<BaseResponse> {
        return this.updateMasterRecord('servingunit', input);
    }

    async updateServingUnit(input: Unit): Promise<BaseResponse> {
        return this.updateMasterRecord('servingunit', input);
    }

    async deleteUnit(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('servingunit', id);
    }

    async deleteServingUnit(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('servingunit', id);
    }
    //#endregion 

    //#region Nutrient (FSSAI Nutrient Master — PostgreSQL)
    async getNutrients(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(
            environment.baseUrl + 'api/FssaiNutrientMaster/list',
            paginationRequest,
            { headers: this.getMasterHeaders() }
        ).toPromise() as BaseResponse;
    }

    async addNutrient(input: any): Promise<BaseResponse> {
        return await this.http.post(
            environment.baseUrl + 'api/FssaiNutrientMaster',
            input,
            { headers: this.getMasterHeaders() }
        ).toPromise() as BaseResponse;
    }

    async updateNutrient(input: any): Promise<BaseResponse> {
        return await this.http.put(
            environment.baseUrl + 'api/FssaiNutrientMaster',
            input,
            { headers: this.getMasterHeaders() }
        ).toPromise() as BaseResponse;
    }

    async deleteNutrient(id: string): Promise<BaseResponse> {
        return await this.http.delete(
            environment.baseUrl + 'api/FssaiNutrientMaster/' + id,
            { headers: this.getMasterHeaders() }
        ).toPromise() as BaseResponse;
    }

    async getFssaiNutrientDropdown(): Promise<BaseResponse> {
        return await this.http.get(
            environment.baseUrl + 'api/FssaiNutrientMaster/dropdown',
            { headers: this.getMasterHeaders() }
        ).toPromise() as BaseResponse;
    }

    async getItemUomList(): Promise<BaseResponse> {
        return await this.http.get(
            environment.baseUrl + 'api/FssaiNutrientMaster/uom-list',
            { headers: this.getMasterHeaders() }
        ).toPromise() as BaseResponse;
    }

    // FSSAI Nutrient Tag Master
    async getFssaiNutrientTags(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(
            environment.baseUrl + 'api/FssaiNutrientTagMaster/list',
            paginationRequest,
            { headers: this.getMasterHeaders() }
        ).toPromise() as BaseResponse;
    }

    async addFssaiNutrientTag(input: any): Promise<BaseResponse> {
        return await this.http.post(
            environment.baseUrl + 'api/FssaiNutrientTagMaster',
            input,
            { headers: this.getMasterHeaders() }
        ).toPromise() as BaseResponse;
    }

    async updateFssaiNutrientTag(input: any): Promise<BaseResponse> {
        return await this.http.put(
            environment.baseUrl + 'api/FssaiNutrientTagMaster',
            input,
            { headers: this.getMasterHeaders() }
        ).toPromise() as BaseResponse;
    }

    async deleteFssaiNutrientTag(id: string): Promise<BaseResponse> {
        return await this.http.delete(
            environment.baseUrl + 'api/FssaiNutrientTagMaster/' + id,
            { headers: this.getMasterHeaders() }
        ).toPromise() as BaseResponse;
    }
    //#endregion

    //#region UOM Master (ItemUomMasters)
    async getIngredientUnitOfMeasurements(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('itemuom', paginationRequest);
    }

    async addIngredientUnitOfMeasurement(input: IngredientUnitOfMeasurement): Promise<BaseResponse> {
        return this.addMasterRecord('itemuom', input);
    }

    async updateIngredientUnitOfMeasurement(input: IngredientUnitOfMeasurement): Promise<BaseResponse> {
        return this.updateMasterRecord('itemuom', input);
    }

    async deleteIngredientUnitOfMeasurement(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('itemuom', id);
    }
    //#endregion

    //#region Database Source
    async getDatabaseSources(input: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/GetDatabaseSources', input)
            .toPromise() as Promise<BaseResponse>;
    }

    async addDatabaseSource(input: any): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/AddDatabaseSource', input)
            .toPromise() as Promise<BaseResponse>;
    }

    async updateDatabaseSource(input: any): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/UpdateDatabaseSource', input)
            .toPromise() as Promise<BaseResponse>;
    }

    async deleteDatabaseSource(id: string): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/DeleteDatabaseSource', id)
            .toPromise() as Promise<BaseResponse>;
    }
    //#endregion

    //#region Health Color Code
    async getHealthColorCodes(input: PaginationRequest): Promise<BaseResponse> {
        return this.listMasterRecords('healthcolor', input);
    }

    async addHealthColorCode(input: HealthColorCode): Promise<BaseResponse> {
        return this.addMasterRecord('healthcolor', input);
    }

    async updateHealthColorCode(input: HealthColorCode): Promise<BaseResponse> {
        return this.updateMasterRecord('healthcolor', input);
    }

    async deleteHealthColorCode(id: string): Promise<BaseResponse> {
        return this.deleteMasterRecord('healthcolor', id);
    }
    //#endregion

    //#region Data Source
    async getDataSources(input: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/GetDataSources', input)
            .toPromise() as BaseResponse;
    }

    async addDataSource(input: any): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/AddDataSource', input)
            .toPromise() as BaseResponse;
    }

    async updateDataSource(input: any): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/UpdateDataSource', input)
            .toPromise() as BaseResponse;
    }

    async deleteDataSource(id: string): Promise<BaseResponse> {
        return await this.http.post(environment.masterApiUrl + '/Master/DeleteDataSource', id)
            .toPromise() as BaseResponse;
    }
    //#endregion

    //#region Raw Ingredients
    async uploadBulkRawIngredients(file: File): Promise<BaseResponse> {
        const formData = new FormData();
        formData.append('file', file, file.name);

        return await this.http.post(environment.apiUrl + '/Master/BulkUploadIngredients', formData)
            .toPromise() as BaseResponse;
    }

    async saveBulkRawIngredients(ingredients: any[]): Promise<BaseResponse> {
        const rows = Array.isArray(ingredients) ? ingredients : [];
        if (!rows.length) {
            return { success: false, status: -1, message: 'No ingredients provided.' } as BaseResponse;
        }

        // One-shot save for the full batch (backend bulk path is optimized).
        // Long timeout — 500 rows with relations can take a while on local DB.
        return await this.http
            .post(environment.apiUrl + '/v1/recipes-pg/ingredients/bulk-save', rows)
            .pipe(timeout(600000))
            .toPromise() as BaseResponse;
    }

    async getRawIngredients(searchString: string = '', pageNo: number = 1, pageSize: number = 50): Promise<BaseResponse> {
        console.log('API Call - Get Raw Ingredients');
        console.log('Request URL:', environment.apiUrl + '/Master/GetIngredients');

        const requestBody: PaginationRequest = {
            pageSize: pageSize,
            pageNo: pageNo,
            searchString: searchString
        };

        console.log('📤 Request Body:', requestBody);

        try {
            const response = await this.http.post(environment.apiUrl + '/Master/GetIngredients', requestBody)
                .toPromise() as BaseResponse;

            console.log('Success with /Master/GetIngredients');
            console.log('Response:', response);
            return response;
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    }
    //#endregion

    //#region Recipe Metadata
    async getRecipeMetadata(include?: string): Promise<any> {
        const cacheKey = include || '__all__';
        const cached = this.recipeMetadataCache.get(cacheKey);
        if (cached) {
            return cached;
        }

        const headers = this.getMasterHeaders();
        const url = include
            ? `${environment.baseUrl}api/recipes/metadata?include=${include}`
            : `${environment.baseUrl}api/recipes/metadata`;
        const request = this.http.get(url, { headers }).toPromise();
        this.recipeMetadataCache.set(cacheKey, request);
        return request;
    }

    /** Drop in-memory metadata cache (e.g. after master data seed / navigation). */
    clearRecipeMetadataCache(include?: string): void {
        if (include) {
            this.recipeMetadataCache.delete(include);
            return;
        }
        this.recipeMetadataCache.clear();
    }
    //#endregion

    //#region Product Metadata
    async getProductMetadata(include?: string): Promise<any> {
        const cacheKey = include || '__all__';
        const cached = this.productMetadataCache.get(cacheKey);
        if (cached) {
            return cached;
        }

        const headers = this.getMasterHeaders();
        const url = include
            ? `${environment.baseUrl}api/products/metadata?include=${include}`
            : `${environment.baseUrl}api/products/metadata`;
        const request = this.http.get(url, { headers }).toPromise();
        this.productMetadataCache.set(cacheKey, request);
        return request;
    }

    /** Drop in-memory product metadata cache (e.g. after Product Type master CRUD). */
    clearProductMetadataCache(include?: string): void {
        if (include) {
            this.productMetadataCache.delete(include);
            return;
        }
        this.productMetadataCache.clear();
    }

    /** PostgreSQL location cascade — states for a country. */
    async getProductStatesByCountry(countryId: number | string): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.get(
            `${environment.baseUrl}api/products/metadata/states/${countryId}`,
            { headers }
        ).toPromise();
    }

    /** PostgreSQL location cascade — cities for a state. */
    async getProductCitiesByState(stateId: number | string): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.get(
            `${environment.baseUrl}api/products/metadata/cities/${stateId}`,
            { headers }
        ).toPromise();
    }

    /** PostgreSQL location cascade — pincodes for a city. */
    async getProductPincodesByCity(cityId: number | string): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.get(
            `${environment.baseUrl}api/products/metadata/pincodes/${cityId}`,
            { headers }
        ).toPromise();
    }

    /** Search pincode and return country/state/city hierarchy for auto-fill. */
    async searchLocationsByPincode(query: string, limit: number = 20): Promise<any> {
        const headers = this.getMasterHeaders();
        const q = encodeURIComponent((query || '').trim());
        return await this.http.get(
            `${environment.baseUrl}api/products/metadata/pincodes/search?query=${q}&limit=${limit}`,
            { headers }
        ).toPromise();
    }

    async getLocationByPincodeId(pincodeId: number | string): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.get(
            `${environment.baseUrl}api/products/metadata/pincodes/lookup/${pincodeId}`,
            { headers }
        ).toPromise();
    }
    //#endregion

    //#region Recipe UOM (Reework parity)
    async getRecipeUomCategories(): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.get(
            environment.baseUrl + 'api/v1/recipes-pg/recipe-uom/categories',
            { headers }
        ).toPromise();
    }

    async getRecipeUomSubcategoriesByCategoryId(categoryId: number): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/recipe-uom/categories/${categoryId}/subcategories`,
            { headers }
        ).toPromise();
    }

    async getRecipeUomMasterByCategory(categoryId: number, subcategoryId?: number): Promise<any> {
        const headers = this.getMasterHeaders();
        let url = environment.baseUrl + `api/v1/recipes-pg/recipe-uom/master/${categoryId}`;
        if (subcategoryId && subcategoryId > 0) {
            url += `?subcategoryId=${subcategoryId}`;
        }
        return await this.http.get(url, { headers }).toPromise();
    }

    async getRecipeUomMappingsByRecipeId(recipeId: number): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/recipe-uom/mappings/${recipeId}`,
            { headers }
        ).toPromise();
    }

    async saveRecipeUomMappings(recipeId: number, mappings: any[]): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + `api/v1/recipes-pg/recipe-uom/mappings/${recipeId}`,
            mappings,
            { headers }
        ).toPromise();
    }
    //#endregion

    //#region Recipe CRUD
    async createRecipe(payload: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg',
            payload,
            { headers }
        ).toPromise();
    }

    /**
     * Bulk-create many recipes in one request (RDMS bulk upload).
     * POST api/v1/recipes-pg/bulk-create
     */
    async bulkCreateRecipes(items: Array<{
        recipe: Record<string, unknown>;
        recipeUoms?: Array<{ uomId: number; valueGram?: number; isPreferred?: boolean }>;
        assignment?: {
            mode?: string;
            makerEmail?: string | null;
            checkerEmail?: string | null;
            dueDate?: string | null;
            priority?: string;
            notes?: string | null;
        } | null;
    }>): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/bulk-create',
            { items },
            { headers }
        ).toPromise();
    }

    async updateRecipe(payload: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.put(
            environment.baseUrl + 'api/v1/recipes-pg',
            payload,
            { headers }
        ).toPromise();
    }

    /** One request for many recipe details (bulk-edit XLSX/CSV download / upload preview). */
    async bulkGetRecipes(ids: Array<number | string>): Promise<any> {
        const uniqueIds = Array.from(
            new Set(
                (ids || [])
                    .map((id) => Number(id))
                    .filter((id) => Number.isFinite(id) && id > 0)
            )
        );
        if (!uniqueIds.length) {
            return { success: false, status: -1, message: 'No recipe ids provided.' };
        }
        return await this.http
            .post(
                environment.baseUrl + 'api/v1/recipes-pg/bulk-get',
                { ids: uniqueIds },
                { headers: this.getMasterHeaders() }
            )
            .pipe(timeout(600000))
            .toPromise();
    }

    /** One request for many full recipe updates (bulk-edit preview confirm). */
    async bulkUpdateRecipes(rows: any[]): Promise<any> {
        const payload = Array.isArray(rows) ? rows : [];
        if (!payload.length) {
            return { success: false, status: -1, message: 'No recipes provided.' };
        }
        return await this.http
            .put(environment.baseUrl + 'api/v1/recipes-pg/bulk', payload, {
                headers: this.getMasterHeaders(),
            })
            .pipe(timeout(600000))
            .toPromise();
    }

    async deleteRecipe(id: number): Promise<any> {
        const headers = {};
        return await this.http.delete(
            environment.baseUrl + `api/v1/recipes-pg/${id}`,
            { headers }
        ).toPromise();
    }

    async searchRecipes(payload: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/search',
            payload,
            { headers }
        ).toPromise();
    }

    async getRecipeById(id: number): Promise<any> {
        const headers = {};
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/${id}`,
            { headers }
        ).toPromise();
    }

    async getMasterRecipeForAdmin(id: number): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/superadmin/master-recipes/${id}`,
            { headers }
        ).toPromise();
    }

    async updateMasterRecipeForAdmin(id: number, payload: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.put(
            environment.baseUrl + `api/v1/recipes-pg/superadmin/master-recipes/${id}`,
            payload,
            { headers }
        ).toPromise();
    }

    async checkRecipeNameExists(name: string, excludeRecipeId?: number): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/check-name',
            { name, excludeRecipeId },
            { headers }
        ).toPromise();
    }

    async checkIngredientDuplicates(payload: {
        ingredientName?: string;
        scientificName?: string;
        dataSource?: string;
        sourcePlatform?: string;
        searchAllSources?: boolean;
        brandName?: string;
        insNumber?: string;
        uniqueIdentifier?: string;
        aliases?: string[];
        commonNames?: string[];
        regionalNames?: string[];
        excludeIngredientId?: number | string;
    }): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/ingredients/check-duplicates',
            payload,
            { headers }
        ).toPromise();
    }
    //#endregion

    //#region Recipe Media Upload
    async uploadRecipeMedia(files: File[], folderName: string = 'recipe-media'): Promise<string[]> {
        const formData = new FormData();
        for (const file of files) {
            formData.append('files', file, file.name);
        }
        formData.append('folderName', folderName);

        const headers = {};
        const res: any = await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/media/upload-multiple',
            formData,
            { headers }
        ).toPromise();

        if (res && res.status === 0 && res.urls) {
            return res.urls as string[];
        }
        return [];
    }
    //#endregion

    //#region Recipe Workflow
    async submitRecipeForReview(recipeId: number): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + `api/v1/recipes-pg/${recipeId}/submit-for-review`,
            {},
            { headers }
        ).toPromise();
    }

    async requestRecipeCorrections(recipeId: number, remark: string): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + `api/v1/recipes-pg/${recipeId}/request-corrections`,
            { remark },
            { headers }
        ).toPromise();
    }

    async markRecipeCorrectionsDone(recipeId: number): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + `api/v1/recipes-pg/${recipeId}/mark-corrections-done`,
            {},
            { headers }
        ).toPromise();
    }

    async approveRecipe(recipeId: number): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + `api/v1/recipes-pg/${recipeId}/approve`,
            {},
            { headers }
        ).toPromise();
    }

    async publishRecipe(recipeId: number): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + `api/v1/recipes-pg/${recipeId}/publish`,
            {},
            { headers }
        ).toPromise();
    }

    async redirectRecipeForChanges(recipeId: number, remark: string): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + `api/v1/recipes-pg/${recipeId}/redirect-for-changes`,
            { remark },
            { headers }
        ).toPromise();
    }

    async setRecipeStatus(recipeId: number, payload: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + `api/v1/recipes-pg/${recipeId}/status`,
            payload,
            { headers }
        ).toPromise();
    }

    async saveRecipeAsDraftStatus(recipeId: number): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + `api/v1/recipes-pg/${recipeId}/save-as-draft`,
            {},
            { headers }
        ).toPromise();
    }

    async getRecipeStatusHistory(recipeId: number): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/${recipeId}/status-history`,
            { headers }
        ).toPromise();
    }

    async saveRecipeSectionApproval(recipeId: number, payload: {
        sectionNumber: number;
        sectionName?: string;
        approvalStatus: string;
        userEmail?: string;
        userName?: string;
        remarks?: string;
    }): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + `api/v1/recipes-pg/${recipeId}/section-approvals`,
            payload,
            { headers }
        ).toPromise();
    }

    async getRecipeSectionApprovals(recipeId: number): Promise<any> {
        const headers = {};
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/${recipeId}/section-approvals`,
            { headers }
        ).toPromise();
    }

    async getRecipeSectionApprovalHistory(recipeId: number, sectionNumber?: number): Promise<any> {
        const headers = {};
        let url = environment.baseUrl + `api/v1/recipes-pg/${recipeId}/section-approvals/history`;
        if (sectionNumber != null && sectionNumber > 0) {
            url += `?sectionNumber=${sectionNumber}`;
        }
        return await this.http.get(url, { headers }).toPromise();
    }

    async getRecipeReviewHistory(recipeId: number): Promise<any> {
        const headers = {};
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/${recipeId}/review-history`,
            { headers }
        ).toPromise();
    }
    //#endregion

    //#region Recipe Assignment
    async assignRecipeToMaker(payload: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/assignments/assign-maker',
            payload,
            { headers }
        ).toPromise();
    }

    async assignRecipeToChecker(payload: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/assignments/assign-checker',
            payload,
            { headers }
        ).toPromise();
    }

    async assignRecipeToMakerAndChecker(payload: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/assignments/assign-maker-and-checker',
            payload,
            { headers }
        ).toPromise();
    }

    async bulkAssignRecipes(payload: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/assignments/bulk-assign',
            payload,
            { headers }
        ).toPromise();
    }

    async bulkAssignRecipesToMakerAndChecker(payload: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/assignments/bulk-assign-maker-and-checker',
            payload,
            { headers }
        ).toPromise();
    }

    async reassignRecipe(payload: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/assignments/reassign',
            payload,
            { headers }
        ).toPromise();
    }

    async getRecipeAssignmentHistory(recipeId: number): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.get(
            environment.baseUrl + `api/v1/recipes-pg/assignments/history/${recipeId}`,
            { headers }
        ).toPromise();
    }
    //#endregion

    //#region Recipe Role-Based Lists
    async getMakerAssignedRecipes(pagination: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/maker/assigned',
            pagination,
            { headers }
        ).toPromise();
    }

    async getMakerFirstReviewRecipes(pagination: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/maker/first-review',
            pagination,
            { headers }
        ).toPromise();
    }

    async getMakerCorrectionsNeededRecipes(pagination: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/maker/corrections-needed',
            pagination,
            { headers }
        ).toPromise();
    }

    async getMakerCorrectionsDoneRecipes(pagination: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/maker/corrections-done',
            pagination,
            { headers }
        ).toPromise();
    }

    async getMakerRecipesByStatus(pagination: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/maker/by-status',
            pagination,
            { headers }
        ).toPromise();
    }

    //#region Unified Dashboard API
    /**
     * Unified dashboard endpoint — returns recipe data based on the caller's role.
     * Pass a `tab` parameter to specify which tab's data to load.
     * Supports pagination, search, and filters.
     */
    async getRecipeDashboard(request: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/dashboard',
            request,
            { headers }
        ).toPromise();
    }

    /**
     * Returns tab counts (KPI summary) for the current user's role.
     * No request body needed — role is determined from the JWT token.
     */
    async getRecipeDashboardCounts(): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.get(
            environment.baseUrl + 'api/v1/recipes-pg/dashboard/counts',
            { headers }
        ).toPromise();
    }
    //#endregion

    async getCheckerAssignedRecipes(pagination: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/checker/assigned',
            pagination,
            { headers }
        ).toPromise();
    }

    async getCheckerChangesNeededRecipes(pagination: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/checker/changes-needed',
            pagination,
            { headers }
        ).toPromise();
    }

    async getCheckerRejectedRecipes(pagination: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/checker/rejected',
            pagination,
            { headers }
        ).toPromise();
    }

    async getAssignerToBeAssignedRecipes(pagination: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/assigner/to-be-assigned',
            pagination,
            { headers }
        ).toPromise();
    }

    async getAssignerAssignedRecipes(pagination: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/assigner/assigned',
            pagination,
            { headers }
        ).toPromise();
    }

    async getApproverCheckedRecipes(pagination: any): Promise<any> {
        const headers = this.getMasterHeaders();
        return await this.http.post(
            environment.baseUrl + 'api/v1/recipes-pg/approver/checked',
            pagination,
            { headers }
        ).toPromise();
    }
    //#endregion

    //#region Ingredient Master & UOM Conversion Methods (Part 2)
    getIngredientMasters(
        page: number = 1,
        pageSize: number = 15,
        search?: string,
        category?: string,
        isActive?: boolean,
        sourcePlatform?: string,
        tab?: string,
        dataSource?: string,
        includeChildren?: boolean,
        mappingStatus?: string
    ): Observable<any> {
        const headers = this.getMasterHeaders();
        const payload = {
            pageNo: page,
            page: page,
            pageSize: pageSize,
            searchString: (search && search.trim()) ? search.trim() : undefined,
            typeFilter: category || undefined,
            sourcePlatform: sourcePlatform || undefined,
            dataSource: dataSource || undefined,
            statusFilter: isActive !== undefined ? (isActive ? ['Active'] : ['Inactive']) : undefined,
            tab: tab || undefined,
            includeChildren: !!includeChildren,
            mappingStatus: mappingStatus || undefined
        };
        return this.http.post(
            environment.baseUrl + 'api/ingredient-master/paged',
            payload,
            { headers }
        );
    }

    mapFoodFamilies(): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + 'api/ingredient-master/map-families',
            {},
            { headers }
        );
    }

    getIngredientMasterDetail(id: string): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.get(
            environment.baseUrl + `api/ingredient-master/${id}`,
            { headers }
        );
    }

    saveIngredientMaster(data: any): Observable<any> {
        const headers = this.getMasterHeaders();
        const payload: any = {
            canonicalName: data.name,
            materialType: data.materialType || data.category || 'Generic',
            brandStatus: data.brandStatus || 'Generic',
            parentIngredientId: data.parentIngredientId ? Number(data.parentIngredientId) : null,
            preferredSource: data.preferredSource || data.source || 'RDMS',
            mappingStatus: data.mappingStatus || 'Mapped',
            healthColorCodeId: data.healthColorCodeId ? Number(data.healthColorCodeId) : null,
            status: data.isActive !== false ? 'Active' : 'Inactive',
            isActive: data.isActive !== false,
            dietaryPreference: data.dietaryPreference || undefined,
            aliases: data.aliases || undefined,
            commonNames: data.commonNames || undefined,
            regionalNames: data.regionalNames || undefined,
            combinedAliases: data.combinedAliases || undefined
        };
        if (data.id) {
            return this.http.put(
                environment.baseUrl + `api/ingredient-master/${data.id}`,
                payload,
                { headers }
            );
        } else {
            return this.http.post(
                environment.baseUrl + 'api/ingredient-master',
                payload,
                { headers }
            );
        }
    }

    updateIngredientAliases(id: string, aliases: string[], commonNames: string[], regionalNames: string[]): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.put(
            environment.baseUrl + `api/ingredient-master/${id}/aliases`,
            { aliases, commonNames, regionalNames },
            { headers }
        );
    }

    saveFoodDensity(ingredientId: string, data: any): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + `api/ingredient-master/${ingredientId}/density`,
            data,
            { headers }
        );
    }

    saveFoodPortionWeight(ingredientId: string, data: any): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + `api/ingredient-master/${ingredientId}/portion-weight`,
            data,
            { headers }
        );
    }

    deleteFoodPortionWeight(portionId: string): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.delete(
            environment.baseUrl + `api/ingredient-master/portion-weight/${portionId}`,
            { headers }
        );
    }

    toggleIngredientMasterStatus(id: string, isActive: boolean): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + `api/ingredient-master/${id}/toggle-status`,
            isActive,
            { headers }
        );
    }

    getConversionHistory(ingredientId: string): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.get(
            environment.baseUrl + `api/ingredient-master/${ingredientId}/conversion-history`,
            { headers }
        );
    }

    getAvailableUoms(ingredientId: string): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.get(
            environment.baseUrl + `api/ingredient-master/${ingredientId}/available-uoms`,
            { headers }
        );
    }

    testUomConversion(payload: any): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + 'api/ingredient-master/convert-to-grams',
            payload,
            { headers }
        );
    }

    getGlobalUomList(): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.get(
            environment.baseUrl + 'api/ingredient-master/global-uoms',
            { headers }
        );
    }

    getQuantityList(): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.get(
            environment.baseUrl + 'api/ingredient-master/quantities',
            { headers }
        );
    }

    previewUomImport(file: File): Observable<any> {
        const headers = this.getMasterHeaders();
        const formData = new FormData();
        formData.append('file', file, file.name);
        return this.http.post(
            environment.baseUrl + 'api/ingredient-master/uom/preview-import',
            formData,
            { headers }
        );
    }

    importUomMaster(file: File, autoCreateMissing: boolean = true): Observable<any> {
        const headers = this.getMasterHeaders();
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('autoCreateMissing', autoCreateMissing.toString());
        return this.http.post(
            environment.baseUrl + `api/ingredient-master/uom/import-csv?autoCreateMissing=${autoCreateMissing}`,
            formData,
            { headers }
        );
    }

    // --- Source Priority ---
    getSourcePriority(): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.get(
            environment.baseUrl + 'api/ingredient-master/source-priority',
            { headers }
        );
    }

    updateSourcePriority(rules: any[]): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.put(
            environment.baseUrl + 'api/ingredient-master/source-priority',
            rules,
            { headers }
        );
    }

    // --- Review Queue ---
    getReviewQueue(): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.get(
            environment.baseUrl + 'api/ingredient-master/mappings/review',
            { headers }
        );
    }

    approveMapping(mappingId: string, notes?: string): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + `api/ingredient-master/mappings/${mappingId}/approve`,
            { reviewNotes: notes || '' },
            { headers }
        );
    }

    rejectMapping(mappingId: string, notes?: string): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + `api/ingredient-master/mappings/${mappingId}/reject`,
            { reviewNotes: notes || '' },
            { headers }
        );
    }

    createCanonicalFromMapping(mappingId: string, canonicalName: string, notes?: string): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + `api/ingredient-master/mappings/${mappingId}/new-canonical`,
            { canonicalName, reviewNotes: notes || '' },
            { headers }
        );
    }

    // --- Source Mapping CRUD ---
    addSourceMapping(ingredientId: string, data: any): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + `api/ingredient-master/${ingredientId}/source-mapping`,
            data,
            { headers }
        );
    }

    deleteSourceMapping(mappingId: string): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.delete(
            environment.baseUrl + `api/ingredient-master/source-mapping/${mappingId}`,
            { headers }
        );
    }

    // --- Nutrient Profiles & Detailed Nutrition ---
    getNutrientProfiles(ingredientId: string): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.get(
            environment.baseUrl + `api/ingredient-master/${ingredientId}/nutrient-profiles`,
            { headers }
        );
    }

    getIngredientNutrition(ingredientId: string): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.get(
            environment.baseUrl + `api/ingredient-master/${ingredientId}/nutrition`,
            { headers }
        );
    }

    saveIngredientNutrition(ingredientId: string, payload: any): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + `api/ingredient-master/${ingredientId}/nutrition`,
            payload,
            { headers }
        );
    }

    upsertNutrientProfile(ingredientId: string, profile: any): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + `api/ingredient-master/${ingredientId}/nutrient-profile`,
            profile,
            { headers }
        );
    }

    backfillNutrientProfiles(): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + 'api/ingredient-master/nutrient-profiles/backfill',
            {},
            { headers }
        );
    }

    mapParentNutrition(): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + 'api/ingredient-master/map-parent-nutrition',
            {},
            { headers }
        );
    }

    // --- Global UOM CRUD ---
    createGlobalUom(data: any): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.post(
            environment.baseUrl + 'api/ingredient-master/global-uoms',
            data,
            { headers }
        );
    }

    updateGlobalUom(id: string, data: any): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.put(
            environment.baseUrl + `api/ingredient-master/global-uoms/${id}`,
            data,
            { headers }
        );
    }

    deleteGlobalUom(id: string): Observable<any> {
        const headers = this.getMasterHeaders();
        return this.http.delete(
            environment.baseUrl + `api/ingredient-master/global-uoms/${id}`,
            { headers }
        );
    }
    //#endregion

}
